import { useUpdateAgentConfigurationVersion } from '@/queries/agent-configuration-versions';
import { logger } from '@/utils';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import InputNumber from 'antd/es/input-number';
import message from 'antd/es/message';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

const FullWidthInputNumber = styled(InputNumber)`
  width: 100%;
`;

const HelpText = styled.span`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
`}
`;

interface ExecutionConfigSectionProps {
  config: {
    executionConfig?: {
      max_actions?: number;
      planning_interval?: number;
      timeout_minutes?: number;
    };
  };
  versionId?: string;
}

export const ExecutionConfigSection: React.FC<ExecutionConfigSectionProps> = ({
  config,
  versionId,
}) => {
  const [form] = Form.useForm();
  const updateVersionMutation = useUpdateAgentConfigurationVersion();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form values
  useEffect(() => {
    form.setFieldsValue({
      executionConfig: {
        max_actions: config.executionConfig?.max_actions || 50,
        planning_interval: config.executionConfig?.planning_interval || 5,
        timeout_minutes: config.executionConfig?.timeout_minutes || 10,
      },
    });
    setHasChanges(false);
  }, [config, form]);

  const handleFormChange = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!versionId || !hasChanges) return;

    setIsSaving(true);
    try {
      const values = await form.validateFields();

      await updateVersionMutation.mutateAsync({
        versionId,
        data: { executionConfig: values.executionConfig },
      });

      void message.success('Execution configuration saved successfully');
      setHasChanges(false);
    } catch (error) {
      logger.error('Failed to save execution configuration:', error);
      void message.error('Failed to save execution configuration');
    } finally {
      setIsSaving(false);
    }
  }, [versionId, hasChanges, form, updateVersionMutation]);

  const theme = useTheme();

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onFieldsChange={handleFormChange}
      >
        <Form.Item
          name={['executionConfig', 'max_actions']}
          label="Max Actions"
          extra={
            <HelpText>Maximum number of actions the agent can take</HelpText>
          }
        >
          <FullWidthInputNumber min={1} max={100} placeholder="50" />
        </Form.Item>

        <Form.Item
          name={['executionConfig', 'planning_interval']}
          label="Planning Interval"
          extra={<HelpText>Steps between planning phases</HelpText>}
        >
          <FullWidthInputNumber min={1} max={20} placeholder="5" />
        </Form.Item>

        <Form.Item
          name={['executionConfig', 'timeout_minutes']}
          label="Timeout (minutes)"
          extra={<HelpText>Maximum execution time in minutes</HelpText>}
        >
          <FullWidthInputNumber
            min={1}
            max={60}
            placeholder="10"
            suffix="min"
          />
        </Form.Item>
      </Form>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          padding: `${theme.margin}px 0`,
          borderTop: '1px solid #f0f0f0',
          marginTop: `${theme.marginLG}px`,
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!versionId || !hasChanges}
          block
        >
          Save Changes
        </Button>
      </div>
    </>
  );
};
